/**
 * Onboarding API Service (T045)
 *
 * Handles all onboarding-related API calls for the customer onboarding tour.
 */

import api, { API_PATHS } from './api';
import type {
  OnboardingProgressResponse,
  OnboardingStepUpdateRequest,
  OnboardingStepUpdateResponse,
  OnboardingCompleteResponse,
  OnboardingCancelResponse,
  OnboardingResumeResponse,
  OnboardingStep,
} from '@/types/onboarding';

/**
 * Onboarding service for managing the onboarding flow.
 */
export const onboardingService = {
  /**
   * Get current onboarding progress.
   */
  async getProgress(): Promise<OnboardingProgressResponse> {
    const response = await api.get<OnboardingProgressResponse>(API_PATHS.ONBOARDING_PROGRESS);
    return response.data;
  },

  /**
   * Update a specific onboarding step.
   */
  async updateStep(
    step: OnboardingStep,
    data: OnboardingStepUpdateRequest
  ): Promise<OnboardingStepUpdateResponse> {
    const response = await api.put<OnboardingStepUpdateResponse>(
      API_PATHS.ONBOARDING_STEP(step),
      data
    );
    return response.data;
  },

  /**
   * Mark onboarding as complete.
   */
  async complete(): Promise<OnboardingCompleteResponse> {
    const response = await api.post<OnboardingCompleteResponse>(API_PATHS.ONBOARDING_COMPLETE);
    return response.data;
  },

  /**
   * Cancel the onboarding process.
   */
  async cancel(): Promise<OnboardingCancelResponse> {
    const response = await api.post<OnboardingCancelResponse>(API_PATHS.ONBOARDING_CANCEL);
    return response.data;
  },

  /**
   * Resume a previously cancelled onboarding.
   */
  async resume(): Promise<OnboardingResumeResponse> {
    const response = await api.post<OnboardingResumeResponse>(API_PATHS.ONBOARDING_RESUME);
    return response.data;
  },

  /**
   * Skip the current onboarding step.
   */
  async skipStep(step: OnboardingStep): Promise<OnboardingStepUpdateResponse> {
    const response = await api.post<OnboardingStepUpdateResponse>(API_PATHS.ONBOARDING_SKIP(step));
    return response.data;
  },

  /**
   * Update company info step data.
   */
  async updateCompanyInfo(data: {
    industry?: string;
    company_size?: string;
    company_website?: string;
    description?: string;
  }): Promise<OnboardingStepUpdateResponse> {
    return this.updateStep('company_info', data);
  },

  /**
   * Update domain setup step data.
   */
  async updateDomainSetup(data: {
    additional_domains?: string[];
    primary_domain_verified?: boolean;
  }): Promise<OnboardingStepUpdateResponse> {
    return this.updateStep('domain_setup', data);
  },

  /**
   * Update brand keywords step data.
   */
  async updateBrandKeywords(data: {
    brand_names: string[];
    additional_keywords?: string[];
    primary_domain?: string;
  }): Promise<OnboardingStepUpdateResponse> {
    return this.updateStep('brand_keywords', data);
  },

  /**
   * Update DMARC setup step data.
   */
  async updateDmarcSetup(data: {
    dmarc_email?: string;
    setup_instructions_viewed?: boolean;
    dmarc_record_verified?: boolean;
  }): Promise<OnboardingStepUpdateResponse> {
    return this.updateStep('dmarc_setup', data);
  },
};

export default onboardingService;
